import { create } from 'zustand'
import type { SelfUser } from '@nexus/shared'
import { api, ApiError, tokens } from '@/lib/api'
import { gateway } from '@/lib/gateway'

interface AuthState {
  user: SelfUser | null
  status: 'loading' | 'authenticated' | 'anonymous'
  error: string | null

  login: (identifier: string, password: string) => Promise<void>
  register: (input: {
    username: string
    displayName: string
    email: string
    password: string
    inviteCode?: string
  }) => Promise<void>
  logout: () => Promise<void>
  restore: () => Promise<void>
  setUser: (user: SelfUser) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'loading',
  error: null,

  login: async (identifier, password) => {
    set({ error: null })
    try {
      const result = await api.auth.login(identifier, password)
      tokens.set(result.accessToken, result.refreshToken)
      set({ user: result.user, status: 'authenticated' })
      gateway.connect()
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Não foi possível entrar'
      set({ error: message })
      throw error
    }
  },

  register: async (input) => {
    set({ error: null })
    try {
      const result = await api.auth.register(input)
      tokens.set(result.accessToken, result.refreshToken)
      set({ user: result.user, status: 'authenticated' })
      gateway.connect()
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Não foi possível criar a conta'
      set({ error: message })
      throw error
    }
  },

  logout: async () => {
    gateway.disconnect()
    try {
      await api.auth.logout()
    } catch {
      // Sair localmente vale mesmo que o servidor não responda.
    }
    tokens.clear()
    set({ user: null, status: 'anonymous' })
  },

  /**
   * Restaura a sessão no carregamento. Se há refresh token guardado, o cliente
   * HTTP renova sozinho na primeira chamada que receber 401.
   */
  restore: async () => {
    if (!tokens.access && !tokens.refresh) {
      set({ status: 'anonymous' })
      return
    }
    try {
      const { user } = await api.users.me()
      set({ user, status: 'authenticated' })
      gateway.connect()
    } catch {
      tokens.clear()
      set({ user: null, status: 'anonymous' })
    }
  },

  setUser: (user) => set({ user }),
  clearError: () => set({ error: null }),
}))
